import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { ExportStatusResponse } from '../types/export';
import apiClient from '../services/api';
import './ExportModal.css';

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  memoirId: string;
  memoirTitle: string;
}

const ExportModal: React.FC<ExportModalProps> = ({ open, onClose, memoirId, memoirTitle }) => {
  const [exportStatus, setExportStatus] = useState<ExportStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [startingPDF, setStartingPDF] = useState(false);
  const [startingEPUB, setStartingEPUB] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingEPUB, setDownloadingEPUB] = useState(false);

  // Fetch export status
  const fetchExportStatus = async () => {
    try {
      const status = await apiClient.getExportStatus(memoirId);
      setExportStatus(status);
      setError(null);

      // Check if any export is in progress
      const isExporting = status.pdf.status === 'in_progress' || status.epub.status === 'in_progress';
      setPollingActive(isExporting);
    } catch (err: any) {
      console.error('Failed to fetch export status:', err);
      setError(err?.detail || 'Failed to load export status');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchExportStatus();
    }
  }, [open, memoirId]);

  // Polling logic - only poll when exports are in progress
  useEffect(() => {
    if (!open || !pollingActive) return;

    const interval = setInterval(() => {
      fetchExportStatus();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [open, pollingActive, memoirId]);

  // Start PDF export
  const handleStartPDFExport = async () => {
    try {
      setError(null);
      setStartingPDF(true);

      // Optimistically update UI to show "in_progress" immediately
      if (exportStatus) {
        setExportStatus({
          ...exportStatus,
          pdf: {
            ...exportStatus.pdf,
            status: 'in_progress',
          }
        });
        // Activate polling since export is now in progress
        setPollingActive(true);
      }

      await apiClient.startPDFExport(memoirId);
      // Don't fetch immediately - let polling handle it to avoid overwriting optimistic update
    } catch (err: any) {
      console.error('Failed to start PDF export:', err);
      setError(err?.detail || 'Failed to start PDF export');
      // Revert optimistic update on error
      await fetchExportStatus();
    } finally {
      setStartingPDF(false);
    }
  };

  // Start EPUB export
  const handleStartEPUBExport = async () => {
    try {
      setError(null);
      setStartingEPUB(true);

      // Optimistically update UI to show "in_progress" immediately
      if (exportStatus) {
        setExportStatus({
          ...exportStatus,
          epub: {
            ...exportStatus.epub,
            status: 'in_progress',
          }
        });
        // Activate polling since export is now in progress
        setPollingActive(true);
      }

      await apiClient.startEPUBExport(memoirId);
      // Don't fetch immediately - let polling handle it to avoid overwriting optimistic update
    } catch (err: any) {
      console.error('Failed to start EPUB export:', err);
      setError(err?.detail || 'Failed to start EPUB export');
      // Revert optimistic update on error
      await fetchExportStatus();
    } finally {
      setStartingEPUB(false);
    }
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    try {
      setError(null);
      setDownloadingPDF(true);
      const blob = await apiClient.downloadPDFExport(memoirId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'debriefr.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
      setError(err?.detail || 'Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Download EPUB
  const handleDownloadEPUB = async () => {
    try {
      setError(null);
      setDownloadingEPUB(true);
      const blob = await apiClient.downloadEPUBExport(memoirId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'debriefr.epub';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download EPUB:', err);
      setError(err?.detail || 'Failed to download EPUB');
    } finally {
      setDownloadingEPUB(false);
    }
  };

  const renderExportCard = (
    format: 'pdf' | 'epub',
    title: string,
    description: string,
    icon: React.ReactNode,
    status: 'not_started' | 'in_progress' | 'completed' | 'failed',
    generatedAt: string | null,
    onStart: () => void,
    onDownload: () => void,
    isStarting: boolean,
    isDownloading: boolean
  ) => {
    return (
      <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          {icon}
          <Box ml={2} flex={1}>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {description}
            </Typography>
          </Box>
        </Box>

        {status === 'not_started' && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={onStart}
            disabled={pollingActive || isStarting}
            startIcon={isStarting ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isStarting ? 'Starting...' : `Generate ${format.toUpperCase()}`}
          </Button>
        )}

        {status === 'in_progress' && (
          <Box display="flex" alignItems="center" justifyContent="center" p={2}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body1" color="textSecondary">
              Generating {format.toUpperCase()}...
            </Typography>
          </Box>
        )}

        {status === 'completed' && (
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body2" color="success.main">
                Generated {generatedAt && new Date(generatedAt).toLocaleString()}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={onDownload}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : `Download ${format.toUpperCase()}`}
              </Button>
              <Button
                variant="outlined"
                onClick={onStart}
                disabled={pollingActive || isStarting}
                startIcon={isStarting ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                {isStarting ? 'Starting...' : 'Regenerate'}
              </Button>
            </Box>
          </Box>
        )}

        {status === 'failed' && (
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <ErrorIcon color="error" sx={{ mr: 1 }} />
              <Typography variant="body2" color="error.main">
                Export failed. Please try again.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={onStart}
              disabled={pollingActive || isStarting}
              startIcon={isStarting ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {isStarting ? 'Starting...' : 'Retry'}
            </Button>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="export-modal"
    >
      <DialogTitle className="export-modal-header">
        <MenuBookIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Export Memoir
      </DialogTitle>

      <DialogContent>
        <Box className="export-modal-content">
          <Typography variant="body2" color="textSecondary" mb={3}>
            Generate professional PDF and EPUB versions of "{memoirTitle}". Files are kept for easy re-download.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : exportStatus ? (
            <>
              {renderExportCard(
                'pdf',
                'PDF (Print-Ready)',
                'Suitable for printing and universal viewing',
                <PictureAsPdfIcon fontSize="large" color="primary" />,
                exportStatus.pdf.status,
                exportStatus.pdf.generated_at,
                handleStartPDFExport,
                handleDownloadPDF,
                startingPDF,
                downloadingPDF
              )}

              {renderExportCard(
                'epub',
                'EPUB (E-Reader)',
                'Compatible with Kindle, Apple Books, Google Play Books',
                <MenuBookIcon fontSize="large" color="primary" />,
                exportStatus.epub.status,
                exportStatus.epub.generated_at,
                handleStartEPUBExport,
                handleDownloadEPUB,
                startingEPUB,
                downloadingEPUB
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="textSecondary" display="block" textAlign="center">
                Files are named "debriefr.pdf" and "debriefr.epub" for consistency
              </Typography>
            </>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={pollingActive}>
          {pollingActive ? 'Generating...' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportModal;
